import React, { useState } from 'react';
import {
  C,
  HUSTON_ITEMS, CFPB_ITEMS, FSES_ITEMS, OVERCONF_ITEMS, LOSS_AV_ITEMS,
  HELP_SEEK_ITEMS, WTP_ITEMS,
  scoreHuston, scoreOverconfidence, scoreLossAversion,
} from '../data/constants';
import { NudgeLogo, ProgressBar, PrimaryBtn, SecondaryBtn } from './shared/UI';

const LIKERT_5 = ['Disagree strongly', 'Disagree a little', 'Neutral', 'Agree a little', 'Agree strongly'];
const FSES_4 = ['Not at all true', 'Slightly true', 'Mostly true', 'Completely true'];

const PT_SECTIONS = [
  { id: 'literacy', title: 'Financial Literacy', desc: 'Choose the best answer for each question.', count: 20 },
  { id: 'wellbeing', title: 'Financial Well-Being', desc: 'How well does each statement describe your situation?', count: 10 },
  { id: 'fses', title: 'Financial Self-Efficacy', desc: 'How true is each statement for you?', count: 6 },
  { id: 'overconf', title: 'Financial Knowledge Estimates', desc: 'Provide your best estimate range (90% confidence interval).', count: 10 },
  { id: 'lossAv', title: 'Financial Decisions', desc: 'Accept or reject each gamble.', count: 6 },
  { id: 'helpSeek', title: 'Professional Advice', desc: 'Rate your agreement with each statement.', count: 8 },
  { id: 'wtp', title: 'Willingness to Pay', desc: 'Select the maximum you would be willing to pay for each service.', count: 3 },
  { id: 'referral', title: 'Advisor Connection', desc: 'Would you like to connect with a qualified financial advisor?', count: 1 },
];

export default function PostTest({ onComplete }) {
  const [section, setSection] = useState(0);
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({
    literacy: [], wellbeing: [], fses: [], overconf: [], lossAv: [],
    helpSeek: [], wtp: [], referral: null,
  });

  const sec = PT_SECTIONS[section];
  const totalProgress = PT_SECTIONS.slice(0, section).reduce((s, x) => s + x.count, 0) + qIdx + 1;
  const totalItems = PT_SECTIONS.reduce((s, x) => s + x.count, 0);

  const setAnswer = (key, idx, val) => {
    setAnswers(a => {
      const arr = [...(a[key] || [])];
      arr[idx] = val;
      return { ...a, [key]: arr };
    });
  };

  const autoNext = (key, max) => {
    setTimeout(() => {
      if (qIdx < max - 1) setQIdx(q => q + 1);
    }, 180);
  };

  const nextSection = () => {
    if (section < PT_SECTIONS.length - 1) {
      setSection(s => s + 1);
      setQIdx(0);
    } else {
      const scores = {
        literacy: scoreHuston(answers.literacy),
        overconfidence: scoreOverconfidence(answers.overconf),
        lossAversion: scoreLossAversion(answers.lossAv),
        helpSeeking: answers.helpSeek,
        wtp: answers.wtp,
        referral: answers.referral,
        raw: answers,
      };
      onComplete(scores);
    }
  };

  const canAdvance = () => {
    if (sec.id === 'referral') return answers.referral != null;
    const arr = answers[sec.id] || [];
    if (sec.id === 'overconf') return arr.filter(x => x?.low != null && x?.high != null).length >= sec.count;
    return arr.filter(v => v != null).length >= sec.count;
  };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: 'rgba(250,251,252,0.96)',
        backdropFilter: 'blur(8px)',
        borderBottom: `1px solid ${C.border}`,
        padding: '12px 24px',
      }}>
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <NudgeLogo size="sm" />
            <span style={{ fontSize: 13, color: C.muted }}>Post-Test Survey — Section {section + 1} of {PT_SECTIONS.length}</span>
          </div>
          <ProgressBar current={totalProgress} total={totalItems} />
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '36px 24px 80px' }}>
        <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 26, color: C.navy, fontWeight: 400, marginBottom: 6 }}>{sec.title}</h2>
        <p style={{ color: C.muted, fontSize: 14, marginBottom: 28 }}>{sec.desc}</p>

        {/* Question progress (for multi-item sections) */}
        {sec.count > 1 && (
          <div style={{ marginBottom: 20 }}>
            <ProgressBar current={qIdx + 1} total={sec.count} label={`Question ${qIdx + 1} of ${sec.count}`} />
          </div>
        )}

        {/* Literacy */}
        {sec.id === 'literacy' && (
          <MultiChoiceCard
            question={HUSTON_ITEMS[qIdx].q}
            options={HUSTON_ITEMS[qIdx].opts}
            selected={answers.literacy[qIdx] ?? null}
            onSelect={v => { setAnswer('literacy', qIdx, v); autoNext('literacy', 20); }}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < 19 && setQIdx(q => q + 1)}
          />
        )}

        {/* Wellbeing */}
        {sec.id === 'wellbeing' && (
          <MultiChoiceCard
            question={CFPB_ITEMS[qIdx].q}
            options={CFPB_ITEMS[qIdx].scale}
            selected={answers.wellbeing[qIdx] ?? null}
            onSelect={v => { setAnswer('wellbeing', qIdx, v); autoNext('wellbeing', 10); }}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < 9 && setQIdx(q => q + 1)}
          />
        )}

        {/* FSES */}
        {sec.id === 'fses' && (
          <MultiChoiceCard
            question={FSES_ITEMS[qIdx]}
            options={FSES_4}
            selected={answers.fses[qIdx] ?? null}
            onSelect={v => { setAnswer('fses', qIdx, v); autoNext('fses', 6); }}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < 5 && setQIdx(q => q + 1)}
          />
        )}

        {/* Overconfidence */}
        {sec.id === 'overconf' && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
            <p style={{ fontSize: 16, color: C.text, fontWeight: 500, lineHeight: 1.6, marginBottom: 10 }}>{OVERCONF_ITEMS[qIdx].q}</p>
            <p style={{ fontSize: 13, color: C.muted, marginBottom: 24 }}>Provide a range you are 90% confident contains the true answer (in {OVERCONF_ITEMS[qIdx].unit}).</p>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {['low', 'high'].map(f => (
                <div key={f} style={{ flex: 1, minWidth: 140 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 6, textTransform: 'capitalize' }}>
                    {f === 'low' ? 'Low Estimate' : 'High Estimate'}
                  </label>
                  <input
                    type="number"
                    value={answers.overconf[qIdx]?.[f] ?? ''}
                    onChange={e => {
                      const arr = [...(answers.overconf || [])];
                      if (!arr[qIdx]) arr[qIdx] = {};
                      arr[qIdx] = { ...arr[qIdx], [f]: parseFloat(e.target.value) || null };
                      setAnswers(a => ({ ...a, overconf: arr }));
                    }}
                    style={{
                      width: '100%', padding: '10px 14px',
                      border: `1px solid ${C.border}`, borderRadius: 8,
                      fontSize: 15, color: C.text, outline: 'none',
                      fontFamily: "'DM Sans', sans-serif",
                    }}
                  />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
              <SecondaryBtn onClick={() => qIdx > 0 && setQIdx(q => q - 1)} style={{ padding: '8px 16px', fontSize: 13 }}>Previous</SecondaryBtn>
              <SecondaryBtn
                onClick={() => qIdx < 9 && setQIdx(q => q + 1)}
                disabled={!(answers.overconf[qIdx]?.low != null && answers.overconf[qIdx]?.high != null)}
                style={{ padding: '8px 16px', fontSize: 13 }}
              >Next</SecondaryBtn>
            </div>
          </div>
        )}

        {/* Loss Aversion */}
        {sec.id === 'lossAv' && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
            <p style={{ fontSize: 14, color: C.muted, marginBottom: 24 }}>Evaluate this gamble and choose to Accept or Reject.</p>
            <div style={{ display: 'flex', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
              <div style={{ flex:1, background:'#dcfce7', border:'1px solid #a7f3d0', borderRadius:10, padding:'20px', textAlign:'center', minWidth:120 }}>
                <p style={{ fontSize:12, color:'#065f46', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>50% CHANCE TO WIN</p>
                <p style={{ fontFamily:"'DM Serif Display', serif", fontSize:32, color:'#059669', margin:0 }}>${LOSS_AV_ITEMS[qIdx].gain}</p>
              </div>
              <div style={{ flex:1, background:'#fee2e2', border:'1px solid #fca5a5', borderRadius:10, padding:'20px', textAlign:'center', minWidth:120 }}>
                <p style={{ fontSize:12, color:'#991b1b', fontWeight:600, textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:8 }}>50% CHANCE TO LOSE</p>
                <p style={{ fontFamily:"'DM Serif Display', serif", fontSize:32, color:C.coral, margin:0 }}>${LOSS_AV_ITEMS[qIdx].loss}</p>
              </div>
            </div>
            <div style={{ display:'flex', gap:12 }}>
              {['accept','reject'].map(v => (
                <button key={v} onClick={() => { setAnswer('lossAv', qIdx, v); autoNext('lossAv', 6); }}
                  style={{
                    flex:1, padding:'13px',
                    border: answers.lossAv[qIdx] === v ? `2px solid ${v==='accept'?C.teal:C.coral}` : `1px solid ${C.border}`,
                    borderRadius:8,
                    background: answers.lossAv[qIdx] === v ? (v==='accept'?C.tealLight:'#fee2e2') : '#fff',
                    color: answers.lossAv[qIdx] === v ? (v==='accept'?C.teal:C.coral) : C.text,
                    fontWeight:600, fontSize:15, cursor:'pointer',
                    fontFamily:"'DM Sans', sans-serif", textTransform:'capitalize',
                  }}
                >{v}</button>
              ))}
            </div>
            <div style={{ display:'flex', justifyContent:'flex-start', marginTop:20 }}>
              <SecondaryBtn onClick={() => qIdx > 0 && setQIdx(q => q - 1)} style={{ padding:'8px 16px', fontSize:13 }}>Previous</SecondaryBtn>
            </div>
          </div>
        )}

        {/* Help-Seeking */}
        {sec.id === 'helpSeek' && (
          <MultiChoiceCard
            question={HELP_SEEK_ITEMS[qIdx]}
            options={LIKERT_5}
            selected={answers.helpSeek[qIdx] ?? null}
            onSelect={v => { setAnswer('helpSeek', qIdx, v); autoNext('helpSeek', 8); }}
            onPrev={() => qIdx > 0 && setQIdx(q => q - 1)}
            onNext={() => qIdx < 7 && setQIdx(q => q + 1)}
          />
        )}

        {/* WTP */}
        {sec.id === 'wtp' && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
            <p style={{ fontSize: 16, color: C.text, fontWeight: 500, lineHeight: 1.6, marginBottom: 24 }}>{WTP_ITEMS[qIdx].q}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
              {WTP_ITEMS[qIdx].opts.map((opt, i) => (
                <button key={i} onClick={() => { setAnswer('wtp', qIdx, opt); autoNext('wtp', 3); }}
                  style={{
                    padding: '10px 18px', borderRadius: 8,
                    border: answers.wtp[qIdx] === opt ? `1.5px solid ${C.teal}` : `1px solid ${C.border}`,
                    background: answers.wtp[qIdx] === opt ? C.tealLight : '#fff',
                    color: answers.wtp[qIdx] === opt ? C.teal : C.text,
                    fontWeight: answers.wtp[qIdx] === opt ? 600 : 400,
                    fontSize: 14, cursor: 'pointer',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >{opt}</button>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <SecondaryBtn onClick={() => qIdx > 0 && setQIdx(q => q - 1)} style={{ padding: '8px 16px', fontSize: 13 }}>Previous</SecondaryBtn>
              <SecondaryBtn onClick={() => qIdx < 2 && setQIdx(q => q + 1)} disabled={answers.wtp[qIdx] == null} style={{ padding: '8px 16px', fontSize: 13 }}>Next</SecondaryBtn>
            </div>
          </div>
        )}

        {/* Advisor referral */}
        {sec.id === 'referral' && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 32 }}>
            <h3 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 22, color: C.navy, fontWeight: 400, marginBottom: 12 }}>
              Connect with a Financial Advisor
            </h3>
            <p style={{ fontSize: 15, color: C.body, lineHeight: 1.7, marginBottom: 28 }}>
              As part of this study, we are offering participants a no-obligation opportunity to connect with
              a qualified, fee-only Certified Financial Planner (CFP). There is no cost or commitment involved.
              You can decide to proceed or not after the initial introduction.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <button
                onClick={() => setAnswers(a => ({ ...a, referral: true }))}
                style={{
                  flex: 1, padding: '14px',
                  border: answers.referral === true ? `2px solid ${C.mint}` : `1px solid ${C.border}`,
                  borderRadius: 8, background: answers.referral === true ? '#f0fdf4' : '#fff',
                  color: answers.referral === true ? '#059669' : C.text,
                  fontWeight: 600, fontSize: 15, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                  minWidth: 160,
                }}
              >
                Yes, connect me
              </button>
              <button
                onClick={() => setAnswers(a => ({ ...a, referral: false }))}
                style={{
                  flex: 1, padding: '14px',
                  border: answers.referral === false ? `2px solid ${C.border}` : `1px solid ${C.border}`,
                  borderRadius: 8, background: answers.referral === false ? '#f8fafc' : '#fff',
                  color: C.body, fontWeight: 500, fontSize: 15, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                  minWidth: 160,
                }}
              >
                No, thank you
              </button>
            </div>
          </div>
        )}

        {/* Section navigation */}
        <div style={{ marginTop: 32, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          {section > 0 && qIdx === 0 && (
            <SecondaryBtn onClick={() => { setSection(s => s - 1); setQIdx(PT_SECTIONS[section-1].count - 1); }}>
              Previous Section
            </SecondaryBtn>
          )}
          <PrimaryBtn onClick={nextSection} disabled={!canAdvance()}>
            {section === PT_SECTIONS.length - 1 ? 'Complete Post-Test' : 'Next Section'}
          </PrimaryBtn>
        </div>
      </div>
    </div>
  );
}

function MultiChoiceCard({ question, options, selected, onSelect, onPrev, onNext }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 28 }}>
      <p style={{ fontSize: 16, color: C.text, fontWeight: 500, lineHeight: 1.6, marginBottom: 24 }}>{question}</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {options.map((opt, i) => (
          <button key={i} onClick={() => onSelect(i)}
            className="select-btn"
            style={{
              padding: '11px 16px', borderRadius: 8,
              border: selected === i ? `1.5px solid ${C.teal}` : `1px solid ${C.border}`,
              background: selected === i ? C.tealLight : '#fff',
              color: selected === i ? C.teal : C.text,
              fontWeight: selected === i ? 600 : 400,
              fontSize: 14, cursor: 'pointer', textAlign: 'left',
              fontFamily: "'DM Sans', sans-serif",
            }}
          >{opt}</button>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 20 }}>
        <SecondaryBtn onClick={onPrev} style={{ padding: '8px 16px', fontSize: 13 }}>Previous</SecondaryBtn>
        <SecondaryBtn onClick={onNext} disabled={selected == null} style={{ padding: '8px 16px', fontSize: 13 }}>Next</SecondaryBtn>
      </div>
    </div>
  );
}
